import type { JSX } from 'react'
import type { GospelMonth } from '../api'
import { monthLabel, yen } from '../format'
import { plotValue } from '../charts/scale'
import { netExPipeline } from '../charts/plan'

// Four headline figures.
//
// Every one of these is a fact about the window on screen, never an
// extrapolation and never a projection: this page's entire argument is that
// the numbers are computed rather than assembled by hand, and a tile that
// quietly invents a trend would undo that.
//
// The "months missing payroll" tile exists because it is the one number that
// tells you how far to trust the other three. The sheet has no equivalent —
// it drags the previous month sideways instead, which is what the parity
// harness reports as 'extrapolated'.

function sum(months: GospelMonth[], pick: (month: GospelMonth) => string | null): number | null {
  let total = 0
  let seen = false
  for (const month of months) {
    const value = plotValue(pick(month))
    if (value === null) continue
    total += value
    seen = true
  }
  return seen ? total : null
}

// Totals are summed in the browser from figures Postgres computed, so they are
// display aggregates of display values — fine for a headline, not a source of
// truth. The tables below carry the per-month figures the DB actually produced.
function asDisplay(total: number | null): string {
  return total === null ? '—' : yen(String(Math.round(total)))
}

export function KpiRow({
  months,
  showPipeline,
}: {
  months: GospelMonth[]
  /** The page's pipeline switch: the net tile follows the charts' basis. */
  showPipeline: boolean
}): JSX.Element | null {
  if (months.length === 0) return null

  const first = months[0]
  const last = months[months.length - 1]
  const arTotal = sum(months, (month) => month.actualAr)
  const netTotal = sum(months, (month) =>
    showPipeline ? month.netCashDifference : netExPipeline(month),
  )
  // Genuinely blank months only: a 'plan' month has payroll behind it (the
  // headcount-plan baseline), so it is not missing.
  const missingPayroll = months.filter((month) => month.apBasis === 'missing').length
  const netNegative = netTotal !== null && netTotal < 0

  return (
    <section className="kpis">
      <article className="kpi accent-blue">
        <p className="kpi-label">Booked AR</p>
        <p className="kpi-value">{asDisplay(arTotal)}</p>
        <p className="kpi-meta">
          {first !== undefined && last !== undefined
            ? `${monthLabel(first.month)} – ${monthLabel(last.month)}`
            : ''}
        </p>
      </article>

      <article className={netNegative ? 'kpi accent-coral' : 'kpi accent-blue'}>
        <p className="kpi-label">Net cash, window</p>
        <p className="kpi-value">{asDisplay(netTotal)}</p>
        <p className="kpi-meta">
          {netNegative ? 'net outflow over the period' : 'net inflow over the period'}
          {showPipeline ? ', incl. weighted pipeline' : ', pipeline excluded'}
        </p>
      </article>

      <article className="kpi accent-yellow">
        <p className="kpi-label">Months shown</p>
        <p className="kpi-value">{months.length}</p>
        <p className="kpi-meta">computed from booked invoices</p>
      </article>

      <article className={missingPayroll > 0 ? 'kpi accent-coral' : 'kpi accent-green'}>
        <p className="kpi-label">Months missing payroll</p>
        <p className="kpi-value">{missingPayroll}</p>
        <p className="kpi-meta">
          {missingPayroll === 0
            ? 'every month has actual or planned payroll'
            : 'AP is neither actual nor planned here'}
        </p>
      </article>
    </section>
  )
}
